import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { TextArea } from '@shared/components/ui/textarea';
import { Label } from '@shared/components/ui/label';
import { useToast } from '@shared/hooks/use-toast';
import { useCreateRate } from '../../hooks/useCreateRate';
import { useActiveOwnBoxes } from '../../hooks/useActiveOwnBoxes';
import { createRateSchema, CreateRateFormData } from '../../schemas/rateSchema';
import { CreateRateItemRequest, RateItem } from '../../models/rate.types';
import { findDuplicateShippingPair } from '../../utils/shippingDuplicateCheck';
import { translateRateError } from '../../utils/rateErrorTranslator';
import { EditableRateItemsTable, TempRateItem } from '../display/EditableRateItemsTable';
import { AddRateItemModal, AddRateItemModalRef } from '../modals/AddRateItemModal';

let nextTempId = 0;

export const CreateRateContainer = () => {
  const { t } = useTranslation('ratesModule');
  const { toast } = useToast();
  const navigate = useNavigate();
  const addItemModalRef = useRef<AddRateItemModalRef>(null);

  const [items, setItems] = useState<TempRateItem[]>([]);

  const { ownBoxes } = useActiveOwnBoxes();

  // Ref to hold the submit function so headerActions doesn't need to change
  const submitRef = useRef<(() => void) | null>(null);

  const { mutateAsync: createRate, isPending } = useCreateRate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateRateFormData>({
    resolver: zodResolver(createRateSchema),
    defaultValues: {
      name: '',
      notes: '',
    },
  });

  const handleAddItem = useCallback(() => {
    addItemModalRef.current?.open();
  }, []);

  const handleItemAdded = useCallback(
    (
      newItem: CreateRateItemRequest & {
        courier_name?: string | null;
        courier_service_name?: string | null;
        country_name?: string | null;
        courier_zone_name?: string | null;
      },
    ) => {
      const tempItem: TempRateItem = {
        id: `temp-${nextTempId++}`,
        title: newItem.title,
        type: newItem.type,
        courier_id: newItem.courier_id ?? null,
        courier_service_id: newItem.courier_service_id ?? null,
        country_id: newItem.country_id ?? null,
        courier_zone_id: newItem.courier_zone_id ?? null,
        courier_name: newItem.courier_name ?? null,
        courier_service_name: newItem.courier_service_name ?? null,
        country_name: newItem.country_name ?? null,
        courier_zone_name: newItem.courier_zone_name ?? null,
        courier: null,
        courier_service: null,
        country: null,
        courier_zone: null,
        data: newItem.data,
        notes: newItem.notes ?? null,
      };
      setItems((prev) => [...prev, tempItem]);
    },
    [],
  );

  const handleRemoveItem = useCallback((id: number | string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleEditItem = useCallback((item: TempRateItem) => {
    addItemModalRef.current?.openEdit(item as RateItem);
  }, []);

  const handleItemEdited = useCallback(
    (
      itemId: number | string,
      updatedItem: CreateRateItemRequest & {
        courier_name?: string | null;
        courier_service_name?: string | null;
        country_name?: string | null;
        courier_zone_name?: string | null;
      },
    ) => {
      setItems((prev) =>
        prev.map((item) => {
          if (item.id !== itemId) return item;
          return {
            ...item,
            title: updatedItem.title,
            type: updatedItem.type,
            courier_id: updatedItem.courier_id ?? null,
            courier_service_id: updatedItem.courier_service_id ?? null,
            country_id: updatedItem.country_id ?? null,
            courier_zone_id: updatedItem.courier_zone_id ?? null,
            courier_name: updatedItem.courier_name ?? null,
            courier_service_name: updatedItem.courier_service_name ?? null,
            country_name: updatedItem.country_name ?? null,
            courier_zone_name: updatedItem.courier_zone_name ?? null,
            data: updatedItem.data,
            notes: updatedItem.notes ?? null,
          };
        }),
      );
    },
    [],
  );

  const onSubmit = useCallback(
    async (formData: CreateRateFormData) => {
      // Convert TempRateItem[] to CreateRateItemRequest[]
      const validItems: CreateRateItemRequest[] = items.map((item) => ({
        title: item.title,
        type: item.type,
        courier_id: item.courier_id ?? null,
        courier_service_id: item.courier_service_id ?? null,
        country_id: item.country_id ?? null,
        courier_zone_id: item.courier_zone_id ?? null,
        data: item.data,
        notes: item.notes ?? null,
      }));

      // Validate at least one item exists
      if (validItems.length === 0) {
        toast({
          title: t('validation.atLeastOneItem'),
          variant: 'destructive',
        });
        return;
      }

      // Validate at least 1 shipping, 1 fulfillment, and 1 storage
      const shippingCount = validItems.filter((item) => item.type === 'shipping').length;
      const fulfillmentCount = validItems.filter((item) => item.type === 'fulfillment').length;
      const storageCount = validItems.filter((item) => item.type === 'storage').length;

      if (shippingCount === 0) {
        toast({
          title: t('validation.requiredShipping'),
          variant: 'destructive',
        });
        return;
      }

      if (fulfillmentCount === 0) {
        toast({
          title: t('validation.requiredFulfillment'),
          variant: 'destructive',
        });
        return;
      }

      if (storageCount === 0) {
        toast({
          title: t('validation.requiredStorage'),
          variant: 'destructive',
        });
        return;
      }

      // Validate only 1 fulfillment and 1 storage per rate
      if (fulfillmentCount > 1) {
        toast({
          title: t('validation.onlyOneFulfillment'),
          variant: 'destructive',
        });
        return;
      }

      if (storageCount > 1) {
        toast({
          title: t('validation.onlyOneStorage'),
          variant: 'destructive',
        });
        return;
      }

      const duplicatePair = findDuplicateShippingPair(validItems);
      if (duplicatePair) {
        toast({
          title: t('validation.duplicateShippingItems'),
          variant: 'destructive',
        });
        return;
      }

      const packagingCount = validItems.filter((item) => item.type === 'packaging').length;

      if (packagingCount > 1) {
        toast({
          title: t('validation.onlyOnePackaging'),
          variant: 'destructive',
        });
        return;
      }

      if (ownBoxes.length > 0 && packagingCount === 0) {
        toast({
          title: t('validation.requiredPackaging'),
          description: t('modal.addItem.packagingHint'),
          variant: 'destructive',
        });
        return;
      }

      try {
        const response = await createRate({
          name: formData.name,
          notes: formData.notes || null,
          items: validItems,
        });

        toast({
          title: t('toast.createSuccess'),
        });

        navigate(`/invoices/rates/${response.id}`);
      } catch (error: any) {
        toast({
          title: t('toast.createError'),
          description: translateRateError(error, t),
          variant: 'destructive',
        });
      }
    },
    [items, ownBoxes, toast, t, createRate, navigate],
  );

  // Update submit ref whenever handleSubmit or onSubmit changes
  useEffect(() => {
    submitRef.current = () => handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  // Breadcrumbs configuration
  const breadcrumbs = useMemo(
    () => [
      { label: t('breadcrumb.invoices', 'Facturación') },
      { label: t('list.title'), path: '/invoices/rates' },
      { label: t('modal.create.title') },
    ],
    [t],
  );

  // Header actions configuration - stable reference
  const headerActions = useMemo(
    () => [
      {
        id: 'cancel-rate',
        label: t('modal.create.cancel'),
        onClick: () => navigate('/invoices/rates'),
        variant: 'outline' as const,
        disabled: isPending,
      },
      {
        id: 'save-rate',
        label: isPending ? t('modal.create.creating') : t('modal.create.submit'),
        onClick: () => submitRef.current?.(),
        variant: 'primary' as const,
        disabled: isPending,
        className: 'text-white',
      },
    ],
    [t, navigate, isPending],
  );

  // Register with navigation system
  usePageNavigation({ breadcrumbs, headerActions });

  return (
    <div className="px-6 py-4 space-y-6">
      <form className="space-y-6">
        {/* Rate info section */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name" className="text-sm font-medium">
              {t('modal.create.name')} *
            </Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  id="name"
                  {...field}
                  placeholder={t('modal.create.namePlaceholder')}
                  className={errors.name ? 'border-error' : ''}
                />
              )}
            />
            {errors.name && <p className="text-xs text-error mt-1">{t(errors.name.message!)}</p>}
          </div>

          <div>
            <Label htmlFor="notes" className="text-sm font-medium">
              {t('modal.create.notes')}
            </Label>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextArea
                  id="notes"
                  {...field}
                  value={field.value || ''}
                  placeholder={t('modal.create.notesPlaceholder')}
                  rows={1}
                  className="resize-none"
                />
              )}
            />
          </div>
        </div>
      </form>

      {/* Items section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-title">{t('modal.create.initialItems')} *</h3>
          <Button type="button" variant="primary" onClick={handleAddItem} className="!text-white">
            {t('actions.addItem')}
          </Button>
        </div>

        {/* Items table with tabs and filters */}
        <EditableRateItemsTable items={items} onRemoveItem={handleRemoveItem} onEditItem={handleEditItem} />
      </div>

      {/* Modal for adding items */}
      <AddRateItemModal
        ref={addItemModalRef}
        rateId={0} // Temporary, not used in creation
        existingItems={items as RateItem[]}
        onItemAdded={handleItemAdded}
        onItemEdited={handleItemEdited}
      />
    </div>
  );
};
